import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  useTheme
} from '@mui/material';

export interface CSVPreviewProps {
  /**
   * En-têtes du CSV
   */
  headers: string[];
  
  /**
   * Données du CSV
   */
  data: Record<string, any>[];
  
  /**
   * Nombre de lignes à afficher
   * @default 5
   */
  rowCount?: number;
  
  /**
   * Mapping des colonnes (optionnel)
   * Si fourni, les colonnes mappées seront mises en évidence
   */
  columnMapping?: Record<string, string>;
  
  /**
   * Titre du tableau
   * @default "Aperçu des données"
   */
  title?: string;
}

/**
 * Composant pour afficher un aperçu des données CSV
 */
const CSVPreview: React.FC<CSVPreviewProps> = ({
  headers,
  data,
  rowCount = 5,
  columnMapping,
  title = "Aperçu des données"
}): React.ReactElement => {
  const theme = useTheme();
  
  // Limiter le nombre de lignes affichées
  const previewData = data.slice(0, rowCount);
  
  // Déterminer les colonnes mappées
  const mappedColumns = columnMapping 
    ? Object.values(columnMapping).filter(Boolean)
    : [];
  
  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      {data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Aucune donnée à afficher
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {/* Colonne pour le numéro de ligne */}
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.grey[100],
                    fontWeight: 'bold',
                    width: '60px'
                  }}
                >
                  Ligne
                </TableCell>
                
                {/* Colonnes pour les en-têtes */}
                {headers.map((header, index) => (
                  <TableCell 
                    key={`header-${index}`}
                    sx={{ 
                      backgroundColor: mappedColumns.includes(header) 
                        ? theme.palette.primary.light 
                        : theme.palette.grey[100],
                      fontWeight: 'bold'
                    }}
                  >
                    {header}
                    {mappedColumns.includes(header) && (
                      <Chip 
                        label={Object.keys(columnMapping!).find(key => columnMapping![key] === header)}
                        size="small"
                        color="primary"
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewData.map((row, rowIndex) => (
                <TableRow key={`row-${rowIndex}`} hover>
                  {/* Numéro de ligne */}
                  <TableCell sx={{ backgroundColor: theme.palette.grey[50] }}>
                    {rowIndex + 1}
                  </TableCell>
                  
                  {/* Cellules de données */}
                  {headers.map((header, cellIndex) => (
                    <TableCell 
                      key={`cell-${rowIndex}-${cellIndex}`}
                      sx={{ 
                        backgroundColor: mappedColumns.includes(header) 
                          ? theme.palette.primary.light + '20' // 20% d'opacité
                          : 'inherit'
                      }}
                    >
                      {row[header] !== undefined && row[header] !== null 
                        ? String(row[header]) 
                        : <span style={{ color: theme.palette.text.disabled }}>-</span>}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {data.length > rowCount && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Affichage de {rowCount} lignes sur {data.length}
        </Typography>
      )}
    </Box>
  );
};

export default CSVPreview;